import * as React from "react"

import { cn } from "@/lib/utils"

type InputSize = "sm" | "default" | "lg"

interface InputProps extends Omit<React.ComponentProps<"input">, "prefix" | "size"> {
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
  size?: InputSize;
  errorText?: React.ReactNode;
  hint?: React.ReactNode;
  success?: boolean;
}

const SIZE_HEIGHTS: Record<InputSize, string> = {
  sm: "h-8 text-xs px-2.5 py-1",
  default: "h-10 text-sm px-3.5 py-2",
  lg: "h-12 text-base px-4 py-2.5",
}

const SIZE_AFFIX: Record<InputSize, string> = {
  sm: "px-2 text-xs",
  default: "px-3 text-sm",
  lg: "px-3.5 text-base",
}

function Input({
  className,
  type,
  suffix,
  prefix,
  size = "default",
  errorText,
  hint,
  success = false,
  id,
  ...props
}: InputProps) {
  const generatedId = React.useId()
  const inputId = id ?? generatedId
  const hintId = `${inputId}-hint`
  const errorId = `${inputId}-error`
  const isInvalid = props["aria-invalid"] === true || !!errorText
  const describedBy = [errorText ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(" ") || undefined

  const sharedInputClass = cn(
    "file:text-foreground placeholder:text-muted-foreground placeholder:transition-opacity placeholder:duration-200 focus:placeholder:opacity-60 flex w-full min-w-0 bg-transparent outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 transition-colors",
    SIZE_HEIGHTS[size],
    className
  )

  const wrapperClass = cn(
    "flex items-stretch rounded-md border bg-card shadow-none transition-all duration-200 overflow-hidden",
    "border-input hover:border-muted-foreground/30 active:border-input/80",

    // focus:border focus:ring-2 focus:ring-muted-foreground/50 focus:outline-none caret-primary

    "focus-within:ring-2 focus-within:ring-muted-foreground/50 focus-within:outline-none caret-primary",
    isInvalid && "border-danger hover:border-danger focus-within:border-danger focus-within:ring-danger/20",
    success && "border-success hover:border-success focus-within:border-success focus-within:ring-success/20"
  )

  if (prefix || suffix) {
    return (
      <div className="flex flex-col gap-1">
        <div className={wrapperClass}>
          {prefix && (
            <span className={cn(
              "flex items-center bg-muted/50 border-r border-input shrink-0 select-none text-muted-foreground",
              SIZE_AFFIX[size]
            )}>
              {prefix}
            </span>
          )}
          <input
            id={inputId}
            type={type}
            data-slot="input"
            aria-invalid={isInvalid || undefined}
            aria-describedby={describedBy}
            className={sharedInputClass}
            {...props}
          />
          {suffix && (
            <span className={cn(
              "flex items-center bg-muted/50 border-l border-input shrink-0 select-none text-muted-foreground",
              SIZE_AFFIX[size]
            )}>
              {suffix}
            </span>
          )}
        </div>
        {errorText && (
          <p id={errorId} role="alert" className="text-xs text-danger flex items-center gap-1">
            {errorText}
          </p>
        )}
        {!errorText && hint && (
          <p id={hintId} className="text-xs text-muted-foreground">{hint}</p>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <input
        id={inputId}
        type={type}
        data-slot="input"
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        className={cn(
          sharedInputClass,
          "rounded-md border transition-all duration-200",
          "border-border hover:border-muted-foreground/30 active:border-input/80",
          "focus:border focus:ring-2 focus:ring-muted-foreground/50 focus:outline-none caret-primary",
          isInvalid && "border-danger hover:border-danger focus-visible:border-danger focus-visible:ring-danger/20",
          success && "border-success hover:border-success focus-visible:border-success focus-visible:ring-success/20"
        )}
        {...props}
      />
      {errorText && (
        <p id={errorId} role="alert" className="text-xs text-danger flex items-center gap-1">
          {errorText}
        </p>
      )}
      {!errorText && hint && (
        <p id={hintId} className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  )
}

export { Input }
